import {APP_INITIALIZER, CSP_NONCE, ErrorHandler, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';
import {LoginComponent} from "./login/login.component";
import {AuthviewComponent} from "./authview/authview.component";
import {NotfoundComponent} from "./notfound/notfound.component";
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptors,
    withInterceptorsFromDi,
    withXhr
} from "@angular/common/http";
import {ErrorInterceptor} from "./_guards/error.interceptor";
import {headersInterceptor} from "./_guards/headers.interceptor";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {TimelineComponent} from "./timeline/timeline.component";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MemoryComponent} from "./memory/memory.component";
import {GoogleMapsModule} from "@angular/google-maps";
import {MemoryFormComponent} from './shared/memory-form/memory-form.component';
import {PeopleComponent} from './people/people.component';
import {FaqsComponent} from './trainingCentre/faqs/faqs.component';
import {ProfileComponent} from './profile/profile.component';
import {PrivacyComponent} from './privacy/privacy.component';
import {TermsComponent} from './terms/terms.component';
import {TimeoutComponent} from './timeout/timeout.component';
import {SettingsComponent} from "./settings/settings.component";
import {LightboxComponent} from './shared/lightbox/lightbox.component';
import {EditItemComponent} from './shared/edit-item/edit-item.component';
import {FeedbackComponent} from './shared/feedback/feedback.component';
import {NoAccessComponent} from './noAccess/noAccess.component';
import {FavouritesComponent} from './favourites/favourites.component';
import {DuplicateFormComponent} from './shared/duplicate-form/duplicate-form.component';
import {SignupComponent} from './signup/signup.component';
import {PassthroughComponent} from "./passthrough/passthrough.component";
import {CommentsComponent} from './shared/comments/comments.component';
import {PasswordResetComponent} from './password-reset/password-reset.component';
import {AcceptInviteComponent} from './accept-invite/accept-invite.component';
import {createErrorHandler, TraceService} from "@sentry/angular";
import {Router} from "@angular/router";
import {DownloadTimelineComponent} from './download-timeline/download-timeline.component';
import {KeyboardShortcutsComponent} from './shared/lightbox/keyboard-shortcuts/keyboard-shortcuts.component';
import {ResourcesComponent} from './trainingCentre/resources/resources.component';
import {VideoguidesComponent} from './trainingCentre/videoguides/videoguides.component';
import {WrittenGuidesComponent} from './trainingCentre/written-guides/written-guides.component';
import {ViewGuideComponent} from './trainingCentre/written-guides/view-guide/view-guide.component';
import {TimeFormatPipe} from "./_pipes/time-format.pipe";
import {StatusPageComponent} from './status-page/status-page.component';
import {HiddenMediaComponent} from './hidden-media/hidden-media.component';
import {DragDropModule} from "@angular/cdk/drag-drop";
import {HiddenMediaDetailsComponent} from './hidden-media/hidden-media-details/hidden-media-details.component';
import {NoboxesComponent} from './noboxes/noboxes.component';
import {FullScreenImageComponent} from './shared/full-screen-image/full-screen-image.component';
import {DropzoneComponent, FileInputDirective} from "@ngx-dropzone/cdk";
import {InfiniteScrollDirective} from "ngx-infinite-scroll";
import {EmojiMenuComponent} from "./shared/emoji-menu/emoji-menu.component";
import {SharedModule} from "./shared/shared.module";
import {UK_DATE_ADAPTER_PROVIDERS} from "./_utils/uk-date-adapter";
import {OverlayScrollbars} from "overlayscrollbars";

const nonce: string = (
    document.querySelector('meta[name="CSP_NONCE"]') as HTMLMetaElement
)?.content;

// Share Angular's CSP nonce with the scrollbar library's generated styles.
OverlayScrollbars.nonce(nonce);

@NgModule({
    declarations: [
        AppComponent,
        LoginComponent,
        NotfoundComponent,
        TimelineComponent,
        MemoryComponent,
        MemoryFormComponent,
        PeopleComponent,
        FaqsComponent,
        ProfileComponent,
        PrivacyComponent,
        TermsComponent,
        TimeoutComponent,
        SettingsComponent,
        LightboxComponent,
        EditItemComponent,
        FeedbackComponent,
        NoAccessComponent,
        FavouritesComponent,
        DuplicateFormComponent,
        SignupComponent,
        PassthroughComponent,
        CommentsComponent,
        PasswordResetComponent,
        AcceptInviteComponent,
        DownloadTimelineComponent,
        KeyboardShortcutsComponent,
        ResourcesComponent,
        VideoguidesComponent,
        WrittenGuidesComponent,
        ViewGuideComponent,
        TimeFormatPipe,
        StatusPageComponent,
        HiddenMediaComponent,
        HiddenMediaDetailsComponent,
        NoboxesComponent,
        FullScreenImageComponent,
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        DragDropModule,
        AuthviewComponent,
        EmojiMenuComponent,
        GoogleMapsModule,
        DropzoneComponent,
        FileInputDirective,
        InfiniteScrollDirective,
        SharedModule,
    ],
    providers: [
        MatDatepickerModule,
        {provide: MAT_DIALOG_DATA, useValue: {}},
        {provide: MatDialogRef, useValue: {}},
        ...UK_DATE_ADAPTER_PROVIDERS,
        {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
        provideHttpClient(withXhr(), withInterceptorsFromDi(), withInterceptors([headersInterceptor])),
        {provide: CSP_NONCE, useValue: nonce},
        {
            provide: ErrorHandler,
            useValue: createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => (): void => {
            },
            deps: [TraceService],
            multi: true,
        },
    ]
})

export class AppModule {
}
